import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { TrendingUp, TrendingDown, HelpCircle, Sparkles, Activity, CheckCircle2, Lock, Clock, Info } from "lucide-react";
import { LockedPanel } from "./entitlements/LockedPanel";
import { LockedRow } from "./entitlements/LockedPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const partyPill = (p) => {
  const c = (p || "").toUpperCase()[0];
  if (c === "D") return "bg-[#2F6BFF]/15 text-blue-300";
  if (c === "R") return "bg-[#FF3B3B]/15 text-rose-300";
  if (c === "I") return "bg-purple-500/15 text-purple-300";
  return "bg-white/5 text-white/60";
};

const verdictStyle = (v) => {
  if (v === "Likely to Pass") return {
    bg: "bg-emerald-500/10", ring: "border-emerald-500/30",
    text: "text-emerald-300", icon: TrendingUp, accent: "text-emerald-400",
  };
  if (v === "Likely to Fail") return {
    bg: "bg-rose-500/10", ring: "border-rose-500/30",
    text: "text-rose-300", icon: TrendingDown, accent: "text-rose-400",
  };
  if (v === "Stalled") return {
    bg: "bg-purple-500/10", ring: "border-purple-500/30",
    text: "text-purple-300", icon: Clock, accent: "text-purple-400",
  };
  return {
    bg: "bg-amber-500/10", ring: "border-amber-500/30",
    text: "text-amber-300", icon: HelpCircle, accent: "text-amber-400",
  };
};

const Row = ({ m }) => (
  <Link
    to={m.legislator?.legislatorId ? `/legislator/${m.legislator.legislatorId}` : "#"}
    className="flex items-center gap-2 text-xs px-2 py-1.5 rounded border border-white/10 bg-white/[0.03] hover:border-orange-500/30 hover:bg-white/[0.06] transition-colors"
  >
    <Badge className={`${partyPill(m.legislator?.party)} border-0 px-1.5 py-0 text-[10px]`}>{m.legislator?.party || "?"}</Badge>
    <span className="font-medium text-white truncate">{m.legislator?.name}</span>
    <span className="text-white/45">{m.legislator?.state}</span>
    <span className="ml-auto tabular-nums font-bold text-orange-400">{Math.round((m.probability || 0) * 100)}%</span>
  </Link>
);

export default function PassageForecast({ billId }) {
  const [state, setState] = useState("loading"); // loading | pending | enacted | already_passed | ready | error
  const [data, setData] = useState(null);
  const [narrativePending, setNarrativePending] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!billId) return;
    setState("loading");
    let stopped = false;
    let attempts = 0;
    const maxAttempts = 25;
    const tick = async () => {
      attempts += 1;
      try {
        const resp = await axios.get(`${API}/bills/${billId}/forecast`, {
          timeout: 40000,
          validateStatus: (s) => s === 200 || s === 202,
        });
        if (stopped) return;
        if (resp.status === 202) {
          setState("pending");
          if (attempts < maxAttempts) setTimeout(tick, 3000);
          return;
        }
        const body = resp.data || {};
        if (body.status === "enacted") { setState("enacted"); setData(body); return; }
        if (body.status === "already_passed") { setState("already_passed"); setData(body); return; }
        setData(body);
        setState("ready");
        if (!body.narrative && attempts < maxAttempts) {
          setNarrativePending(true);
          setTimeout(tick, 3000);
        } else {
          setNarrativePending(false);
        }
      } catch (e) {
        if (stopped) return;
        setState(e?.response?.status === 403 ? "locked" : "error");
      }
    };
    tick();
    return () => { stopped = true; };
  }, [billId, attempt]);

  if (state === "enacted") return null; // hide entirely for enacted bills
  if (state === "error") {
    return (
      <Card className="p-5 mb-6 rounded-xl border border-white/10 flex items-center justify-between gap-4" style={{ background: "var(--whip-surface)" }} data-testid="forecast-error">
        <div>
          <p className="text-[10px] font-mono uppercase tracking-wider text-orange-400/80">WHIP Probability</p>
          <p className="text-sm text-white/70 mt-1">The forecast for this bill could not be loaded. This is usually momentary.</p>
        </div>
        <button type="button" onClick={() => setAttempt((a) => a + 1)}
          className="text-xs font-semibold text-orange-300 hover:text-orange-200 border border-orange-500/30 rounded px-3 py-1.5 transition-colors shrink-0"
          data-testid="forecast-retry">Retry</button>
      </Card>
    );
  }
  if (state === "locked") {
    return <LockedPanel feature="whip_probability" tier="pro" title="WHIP Probability"
      blurb="Passage probability, expected vote count and the plain-language read on what moves this bill." className="mb-6" />;
  }

  // Retrospective "already passed" banner, replaces the forecast with the
  // recorded outcome. If we have both a pre-passage prediction snapshot and
  // the actual tally, render a side-by-side Prediction vs. Reality mini-chart.
  if (state === "already_passed" && data) {
    const passedDate = data.passedAt || "";
    const tally = data.tally;
    const prediction = data.prediction;
    const threshold = data.chamber === "Senate" ? 51 : 218;
    const chamberSize = data.chamber === "Senate" ? 100 : 435;
    const actualTotal = tally ? tally.yes + tally.no : 0;
    const actualYesPct = tally ? (tally.yes / actualTotal) * 100 : 0;
    const predictedYes = prediction?.expectedYes;
    // Predicted tally normalized to the actual vote's total so the bars are comparable
    const predictedYesProjected = predictedYes != null && actualTotal
      ? Math.round((predictedYes / chamberSize) * actualTotal)
      : null;

    return (
      <Card
        className="p-5 mb-6 rounded-xl border border-emerald-500/30"
        style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.10), rgba(11,11,15,0.95))" }}
        data-testid="passage-forecast-already-passed"
      >
        <div className="flex items-start gap-3 mb-3">
          <div className="p-2 rounded-lg bg-emerald-500/15 text-emerald-300">
            <CheckCircle2 className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold mb-1">WHIP Probability</p>
            <h3 className="text-2xl font-bold text-white">
              Passed the {data.chamber || "chamber"}
              {passedDate ? ` on ${passedDate}` : ""}
            </h3>
            {tally && (
              <p className="text-sm text-white/70 mt-1">
                Recorded vote: <span className="font-bold tabular-nums text-emerald-300">{tally.yes}</span> in favor ·{" "}
                <span className="font-bold tabular-nums text-rose-300">{tally.no}</span> against.
              </p>
            )}
          </div>
        </div>

        {/* Prediction vs. Reality mini-chart */}
        {tally && (prediction || true) && (
          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-4 mt-2" data-testid="forecast-pvr">
            <div className="flex items-baseline justify-between mb-3">
              <h4 className="text-sm font-bold text-white">Prediction vs. Reality</h4>
              {prediction?.predictedAt && (
                <span className="text-[10px] text-white/50">
                  predicted {new Date(prediction.predictedAt).toLocaleDateString()}
                </span>
              )}
            </div>

            {prediction ? (
              <div className="space-y-3">
                {/* Predicted row */}
                <div>
                  <div className="flex justify-between text-[11px] text-white/60 mb-1">
                    <span className="flex items-center gap-1.5">
                      <Activity className="w-3 h-3 text-white/50" />
                      <span className="font-semibold text-white/90">Predicted</span>
                      <span className="text-white/50">{prediction.verdict}</span>
                    </span>
                    <span className="tabular-nums">
                      ~{predictedYesProjected} yes · {Math.round((prediction.passProbability || 0) * 100)}% chance
                    </span>
                  </div>
                  <div className="relative h-5 bg-white/5 rounded overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full bg-white/30"
                      style={{ width: `${Math.min(100, ((predictedYesProjected || 0) / actualTotal) * 100)}%` }}
                    />
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-dashed border-orange-400"
                      style={{ left: `${Math.min(100, (threshold / chamberSize) * 100 * (chamberSize / actualTotal))}%` }}
                      title={`Threshold: ${threshold}`}
                    />
                  </div>
                </div>

                {/* Actual row */}
                <div>
                  <div className="flex justify-between text-[11px] text-white/60 mb-1">
                    <span className="flex items-center gap-1.5">
                      <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                      <span className="font-semibold text-emerald-300">Actual</span>
                    </span>
                    <span className="tabular-nums">{tally.yes} yes · {tally.no} no</span>
                  </div>
                  <div className="relative h-5 bg-white/5 rounded overflow-hidden">
                    <div
                      className="absolute left-0 top-0 h-full bg-emerald-500"
                      style={{ width: `${actualYesPct}%` }}
                    />
                    <div
                      className="absolute left-0 top-0 h-full bg-rose-500"
                      style={{
                        left: `${actualYesPct}%`,
                        width: `${(tally.no / actualTotal) * 100}%`,
                      }}
                    />
                  </div>
                </div>

                {/* Delta line */}
                <p className="text-xs text-white/70 pt-2 border-t border-white/5">
                  {predictedYesProjected != null && tally && (
                    <>
                      {Math.abs(tally.yes - predictedYesProjected) <= 3
                        ? <span>Within <span className="font-bold">{Math.abs(tally.yes - predictedYesProjected)}</span> votes of the model.</span>
                        : tally.yes > predictedYesProjected
                          ? <>Actual margin was <span className="font-bold tabular-nums">+{tally.yes - predictedYesProjected}</span> wider than predicted.</>
                          : <>Model overshot yes votes by <span className="font-bold tabular-nums">{predictedYesProjected - tally.yes}</span>.</>
                      }
                    </>
                  )}
                </p>
              </div>
            ) : (
              // No pre-passage snapshot, just show the actual tally bar with context
              <div>
                <div className="flex justify-between text-[11px] text-gray-600 mb-1">
                  <span className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span className="font-semibold text-emerald-800">Final vote</span>
                  </span>
                  <span className="tabular-nums">{tally.yes} yes · {tally.no} no · threshold {threshold}</span>
                </div>
                <div className="relative h-5 bg-gray-100 rounded overflow-hidden">
                  <div className="absolute left-0 top-0 h-full bg-emerald-500" style={{ width: `${actualYesPct}%` }} />
                  <div
                    className="absolute left-0 top-0 h-full bg-rose-400"
                    style={{ left: `${actualYesPct}%`, width: `${(tally.no / actualTotal) * 100}%` }}
                  />
                </div>
                <p className="text-[11px] text-gray-500 mt-2 italic">
                  No pre-passage model snapshot on file for this bill, the forecast tracker started after this vote was taken.
                </p>
              </div>
            )}
          </div>
        )}

        {data.actionText && !tally && (
          <p className="text-xs text-emerald-800/80 mt-2">{data.actionText}</p>
        )}
      </Card>
    );
  }

  const forecast = data?.forecast;
  const loading = state === "loading" || state === "pending";
  const v = forecast?.verdict || "Too close to call";
  const vs = verdictStyle(v);
  const Icon = vs.icon;
  const pct = forecast ? Math.round((forecast.passProbability || 0) * 100) : null;

  return (
    <Card
      className={`p-6 mb-6 rounded-xl border ${vs.ring} ${vs.bg}`}
      data-testid="passage-forecast"
    >
      <div className="flex items-start gap-4 mb-4">
        <div className={`p-2 rounded-lg bg-white/10 border border-white/10 ${vs.accent}`}>
          <Icon className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">WHIP Probability</p>
          </div>
          <h3 className={`text-2xl sm:text-3xl font-bold ${vs.text}`} data-testid="forecast-verdict">
            {loading ? "Running probability…" : v}
          </h3>
          {!loading && forecast && (
            <p className="text-sm text-white/70 mt-1">
              <span className="tabular-nums font-bold text-white">{pct}%</span> chance of clearing the{" "}
              <span className="font-semibold text-white">{forecast.chamber}</span>{" "}
              (expected {forecast.expectedYes} yes · {forecast.expectedNo} no · threshold {forecast.threshold}).
            </p>
          )}
          {!loading && forecast?.modelNotes?.length > 0 && (
            <div className="mt-2 space-y-0.5" data-testid="forecast-model-notes">
              {forecast.modelNotes.map((n, i) => (
                <p key={i} className="text-[11px] text-white/45 flex items-start gap-1.5 leading-relaxed">
                  <Info className="w-3 h-3 mt-0.5 shrink-0 text-white/30" />
                  {n}
                </p>
              ))}
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="bg-white/[0.03] border border-white/5 rounded-lg p-4 text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500 mx-auto mb-2"></div>
          <p className="text-xs text-white/60">Computing per-legislator probabilities…</p>
        </div>
      )}

      {!loading && forecast && (
        <>
          {/* Shift line, how the forecast has moved since the first snapshot */}
          {data?.shift?.summary && (
            <div
              className={`flex items-start gap-2 mb-4 px-3 py-2 rounded-md border text-xs ${
                (data.shift.deltaPP || 0) >= 0.5
                  ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                  : (data.shift.deltaPP || 0) <= -0.5
                    ? "bg-rose-500/10 border-rose-500/30 text-rose-300"
                    : "bg-white/[0.03] border-white/10 text-white/70"
              }`}
              data-testid="forecast-shift"
            >
              <Activity className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span className="leading-relaxed">{data.shift.summary}</span>
            </div>
          )}

          {/* Narrative */}
          {!data?.preview && (
          <div className="bg-white/[0.03] rounded-lg p-4 mb-4 border border-white/10" data-testid="forecast-narrative">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Signal</span>
            </div>
            {data?.narrative ? (
              <p className="text-sm text-white/85 leading-relaxed">{data.narrative}</p>
            ) : (
              <p className="text-sm text-white/45 italic flex items-center gap-2">
                <span className="animate-pulse h-2 w-2 rounded-full bg-orange-400" />
                {narrativePending ? "Generating signal…" : "Signal unavailable"}
              </p>
            )}
          </div>
          )}

          {/* Factors chips */}
          {!data?.preview && (
          <div className="flex flex-wrap gap-1.5 mb-4 text-[11px]">
            <Badge variant="outline" className="bg-white/5 text-white/80 border-white/15">{forecast.factors?.cosponsorCount || 0} cosponsors</Badge>
            {(forecast.factors?.crossPartyCosponsors || 0) > 0 && (
              <Badge variant="outline" className="bg-orange-500/10 text-orange-300 border-orange-500/30">{forecast.factors.crossPartyCosponsors} cross-party</Badge>
            )}
            {forecast.factors?.committeeReported && (
              <Badge variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Reported by committee</Badge>
            )}
            {forecast.factors?.passedOriginatingChamber && (
              <Badge variant="outline" className="bg-[#2F6BFF]/10 text-blue-300 border-[#2F6BFF]/30">Passed originating chamber</Badge>
            )}
            {forecast.factors?.leadershipEndorsed && (
              <Badge variant="outline" className="bg-amber-500/10 text-amber-300 border-amber-500/30">Leadership endorsed</Badge>
            )}
          </div>
          )}

          {/* Leaderboards, locked teaser for Public tier */}
          {data?.preview ? (
            <div className="rounded-lg border border-dashed border-white/15 bg-white/[0.02] p-4" data-testid="forecast-locked-panel">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-3.5 h-3.5 text-orange-400" />
                <span className="text-[10px] uppercase tracking-wider text-white/50 font-bold">WHIP Predictive Intelligence</span>
              </div>
              <div className="space-y-2">
                <LockedRow label="Detailed Vote Forecast" tier="pro" />
                <LockedRow label="Swing Legislators to Watch" tier="pro" />
                <LockedRow label="Reliable Yes / Reliable No" tier="pro" />
                <LockedRow label="Forecast History & Signal" tier="pro" />
              </div>
              <p className="text-[11px] text-white/40 mt-3">
                Included in WHIP Pro, ask your workspace administrator about upgrading.
              </p>
            </div>
          ) : (
          <div>
            <div className="mb-3 flex items-start gap-2 px-3 py-2 rounded-md bg-white/[0.03] border border-white/10 text-[11px] text-white/65 leading-relaxed">
              <Sparkles className="w-3.5 h-3.5 mt-0.5 text-orange-400 shrink-0" />
              <span>
                The <span className="text-white font-semibold">%</span> next to each name is the model's
                estimated probability that <em>this legislator</em> votes <span className="text-emerald-300 font-semibold">yes</span>.
                <span className="text-emerald-300 font-semibold ml-1">Reliable Yes</span> = highest yes-odds in the chamber.
                <span className="text-amber-300 font-semibold ml-1">Swing to watch</span> = closest to 50/50, the votes that decide it.
                <span className="text-rose-300 font-semibold ml-1">Reliable No</span> = lowest yes-odds (most likely to vote no).
              </span>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                <h4 className="text-sm font-bold text-white">Reliable Yes</h4>
              </div>
              <div className="space-y-1.5" data-testid="forecast-reliable-yes">
                {(forecast.reliableYes || []).slice(0, 5).map((m, i) => (
                  <Row key={i} m={m} />
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="w-3.5 h-3.5 text-amber-400" />
                <h4 className="text-sm font-bold text-white">Swing to watch</h4>
              </div>
              <div className="space-y-1.5" data-testid="forecast-swing">
                {(forecast.swingVotes || []).slice(0, 5).map((m, i) => (
                  <Row key={i} m={m} />
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown className="w-3.5 h-3.5 text-rose-400" />
                <h4 className="text-sm font-bold text-white">Reliable No</h4>
              </div>
              <div className="space-y-1.5" data-testid="forecast-reliable-no">
                {(forecast.reliableNo || []).slice(0, 5).map((m, i) => (
                  <Row key={i} m={m} />
                ))}
              </div>
            </div>
          </div>
          </div>
          )}

          {!data?.preview && (
          <details className="mt-4 text-xs text-white/45">
            <summary className="cursor-pointer hover:text-white/70">How this probability is computed</summary>
            <p className="mt-2 leading-relaxed">
              WHIP continuously analyzes legislative activity, sponsorship dynamics, procedural
              progress, chamber composition, and voting history, to estimate each bill's probability
              of passage and each legislator's likely position. Estimates update as new activity is
              recorded. This is a model, it is not a guarantee.
            </p>
          </details>
          )}
        </>
      )}
    </Card>
  );
}
